import React, { useCallback, useMemo } from "react";
import {
    GridColDef,
    GridRenderCellParams,
    GridValueFormatterParams,
    GridValueGetterParams,
} from "@mui/x-data-grid-premium";

import { Base } from "../../../framework/base";
import { AppUtils } from "../../../models/common/appUtils";
import { SalaryRowTypeItem } from "../../../models/salary/salaryRowTypeItem";
import { WorkTimeType } from "../../../models/workShitTimeSlot/workTimeType";
import { WorkTimeDetailsTable } from "./workTimeDetailsTable";
import { useTranslation } from "react-i18next";
import {
    GroupingValue,
} from "../../../store/reportOptionsSlice";
import { useAppSelector } from "../../../framework/customStore";
import { formatTime } from "../../salaries/salariesListGrid";
import { WorkTimeReportGridRow } from "../../../models/reporting/workTimeReport";
import {
    getAllChildRows,
    GroupableDataGrid,
    GroupedRow,
    GroupToggle,
} from "../../framework/groupedDataGrid/groupableDataGrid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { stringOrNumberSortComparator } from "../../framework/dataGrid/sortComparator";

interface GroupableWorkTimeGridProps {
    // vehicles: VehicleListItemDto[];
    // employees: EmployeeListItemDto[];
    salaryRowTypes: SalaryRowTypeItem[];
    // costCenters: ICostCenterItem[];
    // customers: ICustomerItem[];
    workTimeTypes: WorkTimeType[];
    workTimeReportData: WorkTimeReportGridRow[];
}

const GroupingColumn: { [key: string]: keyof WorkTimeReportGridRow } = {
    [GroupingValue.CostCenter]: "costCenterName",
    [GroupingValue.Customer]: "customerName",
    [GroupingValue.Employee]: "employeeName",
    [GroupingValue.Date]: "dateStr",
    [GroupingValue.Vehicle]: "vehicleName",
    [GroupingValue.WorkTimeType]: "workTimeTypeName",
    [GroupingValue.WorkShift]: "workShiftId",
};

export const GroupableWorkTimeGrid = ({
    // vehicles,
    // employees,
    salaryRowTypes,
    // costCenters,
    // customers,
    workTimeTypes,
    workTimeReportData,
}: GroupableWorkTimeGridProps) => {
    const grouping = useAppSelector(
        (state) => state.reportOptions.workTimeGrouping
    );
    const groupByRow = useAppSelector(
        (state) => state.reportOptions.workTimeGroupByRow
    );
    const workTimeGroupingModeMultiple = useAppSelector(
        (state) => state.reportOptions.workTimeGroupingModeMultiple
    );
    const showGroupedRows = useAppSelector(
        (state) => state.reportOptions.workTimeShowGroupedRows
    );
    const groupingModeMultiple = useMemo(
        () => workTimeGroupingModeMultiple || !showGroupedRows,
        [workTimeGroupingModeMultiple, showGroupedRows]
    );
    const { t } = useTranslation();

    const groupingModel = useMemo(
        () =>
            [
                ...grouping.map(
                    (g) =>
                        GroupingColumn[
                            g
                        ] as unknown as keyof WorkTimeReportGridRow
                ),
                (groupByRow
                    ? "id"
                    : null) as unknown as keyof WorkTimeReportGridRow,
            ].filter(Boolean),
        [grouping, groupByRow]
    );

    const gridDef: GridColDef<WorkTimeReportGridRow>[] = useMemo(
        () => [
            {
                field: "duration",
                headerName: t("reporting.total"),
                description: t("reporting.total"),
                groupable: false,
                type: "number",
                minWidth: 100,
                flex: 1,
                valueGetter(params) {
                    return (params.row?.totals?.duration ?? 0) / 60;
                },
                valueFormatter: (params: GridValueFormatterParams<number>) => {
                    const value = params.value;
                    if (!value) return "-";
                    return value.toLocaleFixed(2, "fi-Fi");
                },
            },
            {
                field: "employeeName",
                headerName: t("reporting.employee"),
                description: t("reporting.employee"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "vehicleName",
                headerName: t("reporting.fleet"),
                description: t("reporting.fleet"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "costCenterName",
                headerName: t("reporting.costCenter"),
                description: t("reporting.costCenter"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "workTimeTypeName",
                headerName: t("reporting.workTimeType"),
                description: t("reporting.workTimeType"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "customerName",
                headerName: t("reporting.customer"),
                description: t("reporting.customer"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "id",
                groupable: true,
                type: "string",
                hideable: false,
                valueGetter: ({
                    row: { id, startTime, endTime, dateStr },
                }: GridValueGetterParams<
                    WorkTimeReportGridRow,
                    Partial<WorkTimeReportGridRow>
                >) => ({
                    id,
                    startTime,
                    endTime,
                    dateStr,
                }),
                sortComparator: (
                    v1: Partial<WorkTimeReportGridRow>,
                    v2: Partial<WorkTimeReportGridRow>
                ) => Base.stringCompare(v1.startTime, v2.endTime),
                valueFormatter: ({
                    value,
                }: GridValueFormatterParams<Partial<WorkTimeReportGridRow>>) =>
                    !!value?.startTime
                        ? `${formatTime(
                              value.startTime,
                              value.dateStr
                          )} - ${formatTime(value.endTime, value.dateStr)}`
                        : "-",
            },
            {
                field: "warning",
                groupable: false,
                hideable: false,
                type: "string",
                valueGetter: ({
                    rowNode,
                    api,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) =>
                    getAllChildRows<WorkTimeReportGridRow>(
                        rowNode,
                        api as GridApiPremium
                    ).some((r) => !r.isUpToDate)
                        ? t("reporting.notUpToDate").toString()
                        : null,
            },
            {
                field: "workShiftId",
                groupable: true,
                type: "string",
                hideable: false,
                valueGetter: ({
                    row: { id, workShiftStartTime, workShiftEndTime, dateStr },
                }: GridValueGetterParams<
                    WorkTimeReportGridRow,
                    Partial<WorkTimeReportGridRow>
                >) => ({
                    id,
                    workShiftStartTime,
                    workShiftEndTime,
                    dateStr,
                }),
                sortComparator: (
                    v1: Partial<WorkTimeReportGridRow>,
                    v2: Partial<WorkTimeReportGridRow>
                ) =>
                    Base.stringCompare(
                        v1.workShiftStartTime,
                        v2.workShiftEndTime
                    ),
                valueFormatter: ({
                    value,
                }: GridValueFormatterParams<Partial<WorkTimeReportGridRow>>) =>
                    [
                        !!value.dateStr
                            ? Base.dayjsToDateStrWithWeekday(value.dateStr)
                            : null,
                        !!value?.workShiftStartTime
                            ? `${formatTime(
                                  value.workShiftStartTime,
                                  value.dateStr
                              )} - ${formatTime(
                                  value.workShiftEndTime,
                                  value.dateStr
                              )}`
                            : "-",
                    ]
                        .filter(Boolean)
                        .join(" "),
            },
            {
                field: "dateStr",
                headerName: t("reporting.date"),
                description: t("reporting.date"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                valueGetter: ({
                    rowNode,
                    api,
                    value,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) => {
                    if (rowNode.type === "group") {
                        const childRows =
                            getAllChildRows<WorkTimeReportGridRow>(
                                rowNode,
                                api as GridApiPremium
                            );

                        const dates = Base.getUniqueStringItems(
                            childRows.map((r) => r.dateStr).filter(Boolean)
                        );

                        if (dates.length === 1) {
                            return dates[0];
                        }
                    }

                    return value;
                },
                renderCell: (
                    params: GridRenderCellParams<WorkTimeReportGridRow, string>
                ) =>
                    groupingModeMultiple && params.rowNode?.type === "group" ? (
                        <GroupToggle
                            params={
                                params as unknown as GridRenderCellParams<
                                    GroupedRow<WorkTimeReportGridRow>
                                >
                            }
                            label={
                                !!params.value
                                    ? Base.dayjsToDateStrWithWeekday(
                                        params.value
                                    )
                                    : ""
                            }
                            groupingField="workShiftId"
                        />
                    ) : !!params.value ? (
                        Base.dayjsToDateStrWithWeekday(params.value)
                    ) : (
                        "-"
                    ),
            },
            {
                field: "startTime",
                headerName: t("common.startTime"),
                description: t("common.startTime"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                renderCell: ({
                    value,
                    row,
                    rowNode,
                }: GridRenderCellParams<WorkTimeReportGridRow, string>) =>
                    rowNode.type === "group"
                        ? ""
                        : !!value
                            ? formatTime(value, row.dateStr)
                            : "-",
            },
            {
                field: "endTime",
                headerName: t("common.endTime"),
                description: t("common.endTime"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                renderCell: ({
                    value,
                    row,
                    rowNode,
                }: GridRenderCellParams<WorkTimeReportGridRow, string>) =>
                    rowNode.type === "group"
                        ? ""
                        : !!value
                            ? formatTime(value, row.dateStr)
                            : "-",
            },
            {
                field: "comment",
                headerName: t("reporting.comment"),
                description: t("reporting.comment"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
                valueGetter: ({
                    rowNode,
                    api,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) =>
                    Base.getUniqueStringItems(
                        getAllChildRows<WorkTimeReportGridRow>(
                            rowNode,
                            api as GridApiPremium
                        )
                            .map((r) => r.comment)
                            .filter(Boolean)
                    ).join(", "),
            },
            {
                field: "workShiftStartTime",
                headerName: t("reporting.workShiftStartTime"),
                description: t("reporting.workShiftStartTime"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                valueGetter: ({
                    rowNode,
                    api,
                    value,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) => {
                    if (rowNode.type === "group") {
                        const childRows =
                            getAllChildRows<WorkTimeReportGridRow>(
                                rowNode,
                                api as GridApiPremium
                            );

                        const workShiftIds = Base.getUniqueStringItems(
                            childRows.map((r) => r.workShiftId).filter(Boolean)
                        );

                        if (workShiftIds.length === 1) {
                            return childRows.find(
                                (r) => r.workShiftId === workShiftIds[0]
                            )?.workShiftStartTime;
                        }
                    }

                    return value;
                },
                renderCell: ({
                    value,
                    rowNode,
                }: GridRenderCellParams<WorkTimeReportGridRow, string>) =>
                    !!value
                        ? formatTime(value, value)
                        : rowNode.type === "group"
                            ? ""
                            : "-",
            },
            {
                field: "workShiftEndTime",
                headerName: t("reporting.workShiftEndTime"),
                description: t("reporting.workShiftEndTime"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                valueGetter: ({
                    rowNode,
                    api,
                    value,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) => {
                    if (rowNode.type === "group") {
                        const childRows =
                            getAllChildRows<WorkTimeReportGridRow>(
                                rowNode,
                                api as GridApiPremium
                            );

                        const workShiftIds = Base.getUniqueStringItems(
                            childRows.map((r) => r.workShiftId).filter(Boolean)
                        );

                        if (workShiftIds.length === 1) {
                            return childRows.find(
                                (r) => r.workShiftId === workShiftIds[0]
                            )?.workShiftEndTime;
                        }
                    }

                    return value;
                },
                renderCell: ({
                    value,
                    rowNode,
                }: GridRenderCellParams<WorkTimeReportGridRow, string>) =>
                    !!value
                        ? formatTime(value, value)
                        : rowNode.type === "group"
                            ? ""
                            : "-",
            },
            ...salaryRowTypes.map(
                (srt): GridColDef<WorkTimeReportGridRow> => ({
                    field: srt.id,
                    headerName: `${srt.name} (${srt.measureUnit})`,
                    description: `${srt.name} (${srt.measureUnit})`,
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    valueGetter(params) {
                        const divisor = AppUtils.isTimeUnit(srt.measureUnit)
                            ? 60
                            : 1;
                        return (params.row?.totals?.[srt.id] ?? 0) / divisor;
                    },
                    valueFormatter: (
                        params: GridValueFormatterParams<number>
                    ) => {
                        const value = params.value;
                        if (!value) return "-";
                        return value.toLocaleFixed(srt.decimals ?? 2, "fi-Fi");
                    },
                })
            ),
            ...workTimeTypes.map(
                (wtt): GridColDef<WorkTimeReportGridRow> => ({
                    field: wtt.workTimeTypeId ?? "wtt_null",
                    headerName: wtt.name,
                    description: wtt.name,
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    valueGetter(params) {
                        return (
                            (params.row?.totals?.[
                                wtt.workTimeTypeId ?? "wtt_null"
                            ] ?? 0) / 60
                        );
                    },
                    valueFormatter: (
                        params: GridValueFormatterParams<number>
                    ) => {
                        const value = params.value;
                        if (!value) return "-";
                        return value.toLocaleFixed(2, "fi-Fi");
                    },
                })
            ),
        ],
        [salaryRowTypes, workTimeTypes, groupingModel, groupingModeMultiple, t]
    );

    const pinnedColumns = useMemo(
        () => [
            ...(groupingModeMultiple && grouping.includes("workShiftId")
                ? ["dateStr", "workShiftStartTime", "workShiftEndTime"]
                : []),
            ...(groupingModeMultiple && groupByRow
                ? ["dateStr", "startTime", "endTime"]
                : []),
            "duration",
        ],
        [grouping, groupByRow, groupingModeMultiple]
    );

    const hiddenColumns = useMemo(() => ["id", "workShiftId", "warning"], []);

    const getGroupingColLabel = useCallback(
        (value: any, field: string) => {
            if (field === "id") {
                const wh = workTimeReportData?.find((w) => w.id === value);

                if (!!wh) {
                    const showDate = !grouping.includes(GroupingValue.Date);

                    return [
                        showDate
                            ? Base.dayjsToDateStrWithWeekday(wh.dateStr)
                            : null,
                        !!wh.startTime
                            ? `${formatTime(
                                  wh.startTime,
                                  wh.dateStr
                              )} - ${formatTime(wh.endTime, wh.dateStr)}`
                            : wh.salaryRows.find((sr) => !!sr.salaryRowTypeName)
                                ?.salaryRowTypeName,
                    ]
                        .filter(Boolean)
                        .join(" ");
                }
            } else if (field === "workShiftId") {
                const wh = workTimeReportData?.find(
                    (w) => w.workShiftId === value
                );

                const showDate = !grouping.includes(GroupingValue.Date);

                return [
                    showDate && !!wh
                        ? Base.dayjsToDateStrWithWeekday(wh.dateStr)
                        : null,
                    !!wh?.workShiftStartTime
                        ? `${formatTime(
                              wh.workShiftStartTime,
                              wh.dateStr
                          )} - ${formatTime(wh.workShiftEndTime, wh.dateStr)}`
                        : "-",
                ]
                    .filter(Boolean)
                    .join(" ");
            }

            if (field === "dateStr") {
                return Base.dayjsToDateStrWithWeekday(value as string);
            }

            if (!value) {
                if (field === "vehicleName") {
                    return t("reporting.noFleet");
                } else if (field === "costCenterName") {
                    return t("reporting.noCostCenter");
                } else if (field === "workTimeTypeName") {
                    return t("reporting.noWorkTimeType");
                } else if (field === "customerName") {
                    return t("reporting.noCustomer");
                }
            }

            return value as string;
        },
        [grouping, workTimeReportData, t]
    );

    return (
        <GroupableDataGrid
            noRowsText={t("reporting.noWorkTimes")}
            detailsComponent={WorkTimeDetailsTable}
            detailsComponentProps={{ salaryRowTypes }}
            columns={gridDef}
            rows={workTimeReportData}
            groupingColDef={{
                headerName: t("reporting.groupBy"),
            }}
            getGroupLabel={getGroupingColLabel}
            persistStateKey="GroupableWorkTimeGridState"
            pinnedColumns={pinnedColumns}
            hiddenColumns={hiddenColumns}
            enableColumnSelector
            groupBy={groupingModel}
            groupingModeMultiple={groupingModeMultiple}
            showGroupedRows={showGroupedRows}
        />
    );
};
